import { useState, useEffect } from 'react';
import { getReferences, createReference, updateReference, deleteReference, ProjectReference } from '../lib/supabase';

export function useSupabaseReferences(category?: string) {
  const [references, setReferences] = useState<ProjectReference[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadReferences = async () => {
    try {
      setLoading(true);
      const referencesData = await getReferences(category);
      setReferences(referencesData);
      setError(null);
    } catch (err: any) {
      console.error('Error loading references:', err);
      setError(err.message);
      setReferences([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReferences();
  }, [category]);

  const addReference = async (referenceData: Omit<ProjectReference, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const created = await createReference(referenceData);
      await loadReferences();
      return created;
    } catch (err: any) {
      console.error('Error adding reference:', err);
      throw err;
    }
  };

  const updateReferenceItem = async (id: string, referenceData: Partial<ProjectReference>) => {
    try {
      const updated = await updateReference(id, referenceData);
      await loadReferences();
      return updated;
    } catch (err: any) {
      console.error('Error updating reference:', err);
      throw err;
    }
  };

  const deleteReferenceItem = async (id: string) => {
    try {
      await deleteReference(id);
      await loadReferences();
    } catch (err: any) {
      console.error('Error deleting reference:', err);
      throw err;
    }
  };

  return {
    references,
    loading,
    error,
    addReference,
    updateReference: updateReferenceItem,
    deleteReference: deleteReferenceItem,
    refreshReferences: loadReferences
  };
}
